import { useCallback, useEffect, useState } from "react";
import {
  ShieldCheck, AlertTriangle, Download, FileDown, RefreshCw,
  TrendingUp, CheckCircle2, Loader2, Search, ExternalLink, FileText, X,
  BellRing,
} from "lucide-react";
import { toast } from "sonner";
import { adminFetch, adminApiBase } from "./adminUtils";

/**
 * Admin → Alérgenos — EU 1169/2011 compliance panel (Fase 3).
 * 3 tabs: Overview, Restaurants ranking, Audit log.
 */

const LIGHT_CLS = {
  green: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  yellow: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  red: "bg-red-500/15 text-red-400 border-red-500/30",
};

function Light({ light }) {
  return (
    <span className={`inline-block w-2 h-2 rounded-full ${{
      green: "bg-emerald-400",
      yellow: "bg-amber-400",
      red: "bg-red-400",
    }[light] || "bg-slate-400"}`} />
  );
}

function KpiCard({ label, value, hint, icon: Icon, tone = "cyan" }) {
  const toneCls = {
    cyan: "bg-cyan-500/10 text-cyan-400",
    emerald: "bg-emerald-500/10 text-emerald-400",
    amber: "bg-amber-500/10 text-amber-400",
    rose: "bg-rose-500/10 text-rose-400",
  }[tone];
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${toneCls}`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="text-slate-400 text-xs uppercase tracking-wide">{label}</span>
      </div>
      <div className="mt-2 text-2xl font-bold text-white">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}

// ────────────────── Overview tab ──────────────────

function OverviewTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminFetch("/api/admin/allergens/overview")
      .then(setData)
      .catch((e) => toast.error(e.message || "Error"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="allergens-overview-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label="Platos totales"
          value={data.total_dishes.toLocaleString("es-ES")}
          hint={`${data.total_restaurants} restaurantes activos`}
          icon={TrendingUp}
          tone="cyan"
        />
        <KpiCard
          label="Cobertura global"
          value={`${data.coverage_pct}%`}
          hint={`${data.with_allergens} con alérgenos declarados`}
          icon={CheckCircle2}
          tone={data.coverage_pct >= 70 ? "emerald" : "rose"}
        />
        <KpiCard
          label="Verificación humana"
          value={`${data.verified_pct}%`}
          hint={`${data.verified} verificados manualmente`}
          icon={ShieldCheck}
          tone={data.verified_pct >= 70 ? "emerald" : "rose"}
        />
        <KpiCard
          label="Solo IA (sin revisar)"
          value={`${data.ai_only_pct}%`}
          hint={`${data.ai_only} pendientes de verificar`}
          icon={AlertTriangle}
          tone="amber"
        />
      </div>
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
        <div className="text-sm text-slate-300">
          Actividad últimos 30 días:{" "}
          <span className="text-white font-semibold">{data.recent_reviews_30d}</span>{" "}
          revisiones aplicadas (aprobar/rechazar/verificar).
        </div>
      </div>
      <div className="text-xs text-muted-foreground leading-relaxed p-3 rounded-lg border border-slate-800 bg-slate-950/40">
        <strong className="text-slate-400">📜 Reglamento (UE) 1169/2011:</strong>{" "}
        Los operadores de establecimientos deben ofrecer información sobre los
        14 alérgenos declarables en todos los platos de la carta. Los KPIs de
        arriba te dan visibilidad de cumplimiento agregado. Usa la pestaña{" "}
        <em>Restaurantes</em> para identificar los que requieren acción.
      </div>
    </div>
  );
}

// ────────────────── Restaurants tab ──────────────────

function RestaurantsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyBelow, setOnlyBelow] = useState(0);
  const [sortBy, setSortBy] = useState("coverage_pct");
  const [search, setSearch] = useState("");
  const [drillRid, setDrillRid] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillReminders, setDrillReminders] = useState([]);
  const [reminding, setReminding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ sort_by: sortBy });
      if (onlyBelow) q.append("only_below", String(onlyBelow));
      const r = await adminFetch(`/api/admin/allergens/restaurants?${q}`);
      setRows(r.restaurants || []);
    } catch (e) {
      toast.error(e.message || "Error");
    } finally {
      setLoading(false);
    }
  }, [onlyBelow, sortBy]);
  useEffect(() => { load(); }, [load]);

  const openDrill = async (rest) => {
    setDrillRid(rest);
    setDrillLoading(true);
    setDrillData(null);
    setDrillReminders([]);
    try {
      const [pending, remRes] = await Promise.all([
        adminFetch(`/api/admin/allergens/restaurants/${rest.restaurant_id}/pending`),
        adminFetch(`/api/admin/allergens/reminders?restaurant_id=${rest.restaurant_id}&limit=5`),
      ]);
      setDrillData(pending);
      setDrillReminders(remRes.reminders || []);
    } catch (e) {
      toast.error(e.message || "Error cargando platos");
    } finally {
      setDrillLoading(false);
    }
  };

  const sendReminder = async () => {
    if (!drillRid) return;
    if (!window.confirm(`¿Enviar recordatorio al dueño de «${drillRid.name}» sobre los platos con alérgenos pendientes?`)) return;
    setReminding(true);
    try {
      const r = await adminFetch(
        `/api/admin/allergens/restaurants/${drillRid.restaurant_id}/remind`,
        { method: "POST" },
      );
      toast.success(`Recordatorio enviado · ${r.pending_count} plato${r.pending_count !== 1 ? "s" : ""} pendiente${r.pending_count !== 1 ? "s" : ""}`);
      // Refresh the reminders list so the new row appears immediately
      try {
        const rr = await adminFetch(`/api/admin/allergens/reminders?restaurant_id=${drillRid.restaurant_id}&limit=5`);
        setDrillReminders(rr.reminders || []);
      } catch { /* non-critical */ }
    } catch (e) {
      toast.error(e.message || "No se pudo enviar el recordatorio");
    } finally {
      setReminding(false);
    }
  };

  const lastReminderAgo = (() => {
    if (drillReminders.length === 0) return null;
    const iso = drillReminders[0].sent_at;
    if (!iso) return null;
    const diffH = (Date.now() - new Date(iso).getTime()) / 3600000;
    if (diffH < 1) return "hace menos de 1h";
    if (diffH < 24) return `hace ${Math.floor(diffH)}h`;
    return `hace ${Math.floor(diffH / 24)}d`;
  })();

  const filtered = rows.filter((r) =>
    !search ||
    r.name.toLowerCase().includes(search.toLowerCase()) ||
    (r.owner_email || "").toLowerCase().includes(search.toLowerCase()),
  );

  const downloadPdf = (rid, name) => {
    const url = `${adminApiBase()}/api/admin/allergens/restaurants/${rid}/compliance.pdf`;
    window.open(url, "_blank");
    toast.success(`Informe de «${name}» descargado`);
  };

  return (
    <div className="space-y-4" data-testid="allergens-restaurants-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filtrar por nombre o email…"
            className="w-full pl-9 pr-3 py-2 text-sm bg-slate-900 border border-slate-800 rounded-lg text-slate-200 placeholder-slate-600"
          />
        </div>
        <select
          value={onlyBelow}
          onChange={(e) => setOnlyBelow(Number(e.target.value))}
          className="py-2 px-3 text-sm bg-slate-900 border border-slate-800 rounded-lg text-slate-200"
        >
          <option value={0}>Todos los restaurantes</option>
          <option value={70}>Cobertura &lt; 70%</option>
          <option value={90}>Cobertura &lt; 90%</option>
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="py-2 px-3 text-sm bg-slate-900 border border-slate-800 rounded-lg text-slate-200"
        >
          <option value="coverage_pct">Orden: cobertura</option>
          <option value="verified_pct">Orden: verificación</option>
          <option value="total_dishes">Orden: nº platos</option>
        </select>
        <button
          onClick={load}
          className="flex items-center gap-1.5 px-3 py-2 text-xs text-slate-300 border border-slate-700 rounded-lg hover:bg-slate-800"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refrescar
        </button>
      </div>
      <div className="rounded-xl border border-slate-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/60 text-muted-foreground text-[11px] uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">Restaurante</th>
              <th className="px-3 py-2 text-left">Dueño</th>
              <th className="px-3 py-2 text-right">Platos</th>
              <th className="px-3 py-2 text-right">Cobertura</th>
              <th className="px-3 py-2 text-right">Verificación</th>
              <th className="px-3 py-2 text-right">Último check</th>
              <th className="px-3 py-2 text-right">Informe</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {loading ? (
              <tr>
                <td colSpan={7} className="text-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin inline text-muted-foreground" />
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-8 text-muted-foreground text-xs">
                  Sin restaurantes que cumplan el filtro.
                </td>
              </tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.restaurant_id}
                    className="bg-slate-950/40 hover:bg-slate-900/40 transition-colors"
                    data-testid={`rest-row-${r.restaurant_id}`}>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => openDrill(r)}
                      className="text-left hover:text-cyan-300 transition-colors group"
                      data-testid={`rest-drill-${r.restaurant_id}`}
                    >
                      <div className="text-white text-sm font-medium truncate max-w-xs group-hover:underline" title={r.name}>
                        {r.name}
                      </div>
                      <div className="text-[10px] text-muted-foreground font-mono">{r.restaurant_id}</div>
                    </button>
                  </td>
                  <td className="px-3 py-2 text-slate-400 text-xs">
                    {r.owner_email}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-300 text-xs font-mono">
                    {r.total_dishes}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${LIGHT_CLS[r.coverage_light]}`}>
                      <Light light={r.coverage_light} />
                      {r.coverage_pct}%
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${LIGHT_CLS[r.verified_light]}`}>
                      <Light light={r.verified_light} />
                      {r.verified_pct}%
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-muted-foreground text-[11px]">
                    {r.last_verified_at
                      ? new Date(r.last_verified_at).toLocaleDateString("es-ES")
                      : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => downloadPdf(r.restaurant_id, r.name)}
                      data-testid={`rest-pdf-${r.restaurant_id}`}
                      className="inline-flex items-center gap-1 text-[11px] text-cyan-400 hover:text-cyan-300 px-2 py-1 rounded hover:bg-cyan-500/10 transition-colors"
                    >
                      <FileDown className="w-3 h-3" />
                      PDF
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Drill-down side panel */}
      {drillRid && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end"
          onClick={() => setDrillRid(null)}
          data-testid="drilldown-backdrop"
        >
          <div
            className="w-full max-w-lg h-full bg-slate-950 border-l border-slate-800 shadow-2xl overflow-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="drilldown-panel"
          >
            <div className="sticky top-0 bg-slate-950 border-b border-slate-800 px-5 py-4 flex items-start justify-between gap-3 z-10">
              <div className="min-w-0 flex-1">
                <h3 className="text-white font-semibold text-base truncate">
                  {drillRid.name}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${LIGHT_CLS[drillRid.coverage_light]}`}>
                    <Light light={drillRid.coverage_light} />
                    Cobertura {drillRid.coverage_pct}%
                  </span>
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${LIGHT_CLS[drillRid.verified_light]}`}>
                    <Light light={drillRid.verified_light} />
                    Verificación {drillRid.verified_pct}%
                  </span>
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  {drillRid.owner_email}
                </div>
              </div>
              <button
                onClick={() => setDrillRid(null)}
                className="text-muted-foreground hover:text-slate-200 p-1"
                data-testid="drilldown-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-3">
              {/* Recent reminder warning (avoid duplicate outreach) */}
              {lastReminderAgo && (
                <div className="flex items-start gap-2 text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded-md px-2.5 py-2">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>
                    Último recordatorio enviado <b>{lastReminderAgo}</b>
                    {drillReminders[0].sent_by_email && (
                      <> por <b>{drillReminders[0].sent_by_email}</b></>
                    )}.
                    Evita saturar al dueño si ya ha sido contactado recientemente.
                  </span>
                </div>
              )}

              {/* Action button */}
              {drillData && drillData.count > 0 && (
                <button
                  onClick={sendReminder}
                  disabled={reminding}
                  data-testid="drill-remind-btn"
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-500 rounded-lg transition-colors disabled:opacity-60"
                >
                  {reminding ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <BellRing className="w-4 h-4" />
                  )}
                  Enviar recordatorio al dueño
                </button>
              )}

              <div className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                Platos pendientes de verificar
                {drillData && (
                  <span className="ml-1 text-muted-foreground normal-case">
                    ({drillData.count})
                  </span>
                )}
              </div>

              {drillLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : !drillData || drillData.dishes.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground text-sm">
                  <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500/60 mb-2" />
                  Todos los platos están verificados.
                </div>
              ) : (
                <ul className="space-y-1.5">
                  {drillData.dishes.map((d) => (
                    <li key={d.dish_id}
                        className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-slate-900 border border-slate-800"
                        data-testid={`drill-dish-${d.dish_id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-slate-200 truncate">{d.name}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">{d.dish_id}</div>
                      </div>
                      <span className={`inline-block text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full border ${
                        d.status === "not_analyzed"
                          ? "bg-slate-500/15 text-slate-400 border-slate-500/30"
                          : "bg-amber-500/15 text-amber-400 border-amber-500/30"
                      }`}>
                        {d.status === "not_analyzed" ? "Sin analizar" : "Solo IA"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {drillData && drillData.count > drillData.dishes.length && (
                <div className="text-[11px] text-muted-foreground text-center">
                  … mostrando primeros {drillData.dishes.length} de {drillData.count}.
                </div>
              )}

              {/* Reminders history */}
              {drillReminders.length > 0 && (
                <div className="pt-4 border-t border-slate-800 space-y-2" data-testid="drill-reminders-history">
                  <div className="text-xs font-medium text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
                    <BellRing className="w-3 h-3" />
                    Recordatorios enviados
                    <span className="text-muted-foreground normal-case">({drillReminders.length})</span>
                  </div>
                  <ul className="space-y-1">
                    {drillReminders.map((r) => (
                      <li key={r.reminder_id}
                          className="flex items-center justify-between gap-2 text-[11px] text-slate-400 px-2.5 py-1.5 rounded-md bg-slate-900/60 border border-slate-800">
                        <div className="min-w-0 flex-1">
                          <div className="text-slate-300 truncate">{r.sent_by_email || "—"}</div>
                          <div className="text-[10px] text-muted-foreground">
                            {r.sent_at ? new Date(r.sent_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : "—"}
                          </div>
                        </div>
                        <span className="text-[10px] text-muted-foreground shrink-0">
                          {r.pending_count} plato{r.pending_count !== 1 ? "s" : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────── Audit tab ──────────────────

function AuditTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ limit: "200" });
      if (filterAction) q.append("action", filterAction);
      const r = await adminFetch(`/api/admin/allergens/audit?${q}`);
      setEntries(r.entries || []);
    } catch (e) {
      toast.error(e.message || "Error");
    } finally {
      setLoading(false);
    }
  }, [filterAction]);
  useEffect(() => { load(); }, [load]);

  const downloadCsv = () => {
    window.open(`${adminApiBase()}/api/admin/allergens/audit.csv`, "_blank");
    toast.success("Descargando audit log…");
  };

  const actionBadge = (action) => {
    const map = {
      approve: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
      reject: "bg-red-500/15 text-red-400 border-red-500/30",
      verify: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
    };
    return map[action] || "bg-slate-500/15 text-slate-400 border-slate-500/30";
  };

  return (
    <div className="space-y-4" data-testid="allergens-audit-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value)}
          className="py-2 px-3 text-sm bg-slate-900 border border-slate-800 rounded-lg text-slate-200"
        >
          <option value="">Todas las acciones</option>
          <option value="approve">Aprobar</option>
          <option value="reject">Rechazar</option>
          <option value="verify">Verificar</option>
        </select>
        <button
          onClick={load}
          className="flex items-center gap-1.5 px-3 py-2 text-xs text-slate-300 border border-slate-700 rounded-lg hover:bg-slate-800"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refrescar
        </button>
        <div className="flex-1" />
        <button
          onClick={downloadCsv}
          data-testid="audit-download-csv-btn"
          className="flex items-center gap-1.5 px-3 py-2 text-xs text-cyan-300 border border-cyan-500/30 bg-cyan-500/10 rounded-lg hover:bg-cyan-500/20"
        >
          <Download className="w-3.5 h-3.5" />
          Exportar CSV completo
        </button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          Sin entradas que coincidan con el filtro.
        </div>
      ) : (
        <div className="rounded-xl border border-slate-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/60 text-muted-foreground text-[11px] uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left">Fecha</th>
                <th className="px-3 py-2 text-left">Acción</th>
                <th className="px-3 py-2 text-left">Restaurante</th>
                <th className="px-3 py-2 text-left">Plato</th>
                <th className="px-3 py-2 text-left">Usuario</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {entries.map((e, idx) => (
                <tr key={idx} className="bg-slate-950/40 hover:bg-slate-900/40 transition-colors">
                  <td className="px-3 py-2 text-[11px] text-muted-foreground">
                    {e.reviewed_at
                      ? new Date(e.reviewed_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })
                      : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-block text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full border ${actionBadge(e.action)}`}>
                      {e.action}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-400 text-[11px] font-mono truncate max-w-[160px]">
                    {e.restaurant_id}
                  </td>
                  <td className="px-3 py-2 text-slate-400 text-[11px] font-mono truncate max-w-[160px]">
                    {e.dish_id}
                  </td>
                  <td className="px-3 py-2 text-slate-300 text-xs truncate max-w-[180px]">
                    {e.reviewed_by_email || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ────────────────── Main page ──────────────────

const TABS = [
  { id: "overview", label: "Vista general", icon: TrendingUp },
  { id: "restaurants", label: "Restaurantes", icon: ShieldCheck },
  { id: "audit", label: "Audit log", icon: FileText },
];

export default function AdminAllergens() {
  const [tab, setTab] = useState("overview");

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="admin-allergens-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-cyan-500/15 rounded-xl flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-cyan-400" />
        </div>
        <div>
          <h1 className="text-white text-xl font-bold">Alérgenos — Cumplimiento EU 1169</h1>
          <p className="text-muted-foreground text-sm">
            Panel global de la plataforma. Informes, auditoría y estado por restaurante.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-800">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`allergens-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
                tab === t.id
                  ? "border-cyan-400 text-cyan-400"
                  : "border-transparent text-muted-foreground hover:text-slate-300"
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <OverviewTab />}
      {tab === "restaurants" && <RestaurantsTab />}
      {tab === "audit" && <AuditTab />}
    </div>
  );
}
